"use client"

import { useEffect, useRef, type ReactNode } from "react"
import { usePathname } from "next/navigation"
import { MotionConfig } from "framer-motion"

import { cn } from "@/lib/utils"
import { SidebarInset } from "@/components/ui/custom-sidebar"
import { AppSidebar } from "@/components/nav/AppSidebar"
import { AppHeader } from "@/components/layout/AppHeader"
import { AppFooter } from "@/components/layout/AppFooter"
import { MAIN_SCROLLER_ID } from "@/components/layout/scroller"
import { CHROME_MASK, CHROME_FADE_END_PX } from "@/lib/fade"
import { DURATION, EASE } from "@/lib/motion"

/**
 * The one app shell: header, sidebar, and the scrollport pages render into.
 *
 * This replaces the old Template, which took `content`/`topMenu`/`rightSheet`/
 * `bottomMenu` props that every call site passed as null except one — and which
 * several pages rendered a SECOND time inside a layout that had already rendered
 * it, nesting two shells. Children instead of a `content` prop makes that mistake
 * impossible to write.
 *
 * Pages vary the shell through data attributes rather than props, because the
 * layout renders one shell for every route and cannot know which page is below
 * it. See PageChrome in components/layout/page-chrome.tsx:
 *
 *   data-page-width="wide"        content column 3xl → 6xl
 *   data-page-width="full"        no content column; the page draws its own
 *   data-page-layout="bleed"      no column, no scrollport — the page owns the
 *                                 full viewport under the header (the canvas)
 *
 * The footer is not one of these. It appears by being filled — see PageFooterSlot — and the
 * shell reads its emptiness directly, so a page never declares that it has one.
 *
 * Deliberately not transitioned: animating max-width reflows prose every frame,
 * so the text visibly re-wraps its way to the new column width.
 */
/**
 * The pages someone can reach with no account and no access code, and which therefore wear the
 * minimal header: no nav panel behind the wordmark, just a home link, the theme toggle and the
 * language switcher.
 *
 * The nav panel is the reason. It lists Research, Propositions and Bookmarks, and while the
 * pre-launch gate is up every one of those answers with an access-code form — so on these three
 * pages it is a menu of doors that do not open. The list matches PREVIEW_OPEN_PATHS in
 * lib/preview-gate.ts, which is the same set seen from the other side.
 *
 * Kept here as its own list rather than imported from there, because they stop agreeing at
 * launch: when the gate comes down, the other two pages get their nav back and `/` keeps the
 * minimal header, since it is marketing either way and only ever shown to a signed-out visitor
 * (a session is redirected to /research).
 */
const FRONT_OF_HOUSE = new Set(["/", "/privacy", "/terms"])

export function AppShell({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()

  /*
    A new route starts at the top. Without this, navigating from halfway down a judgment lands
    halfway down the next page.

    Unless the URL names somewhere to land. A fragment is a request to arrive at one passage —
    a citation linking into paragraph 89 of another judgment — and resetting to the top would
    answer it by ignoring it. The page that owns the fragment does the scrolling (see
    ParagraphAnchor); all this has to do is not undo it.
  */
  useEffect(() => {
    const container = scrollerRef.current
    if (!container) return
    if (window.location.hash) return
    container.scrollTop = 0
  }, [pathname])

  return (
    /*
      One motion policy for the whole app.

      `reducedMotion="user"` makes every framer-motion animation below here honour the operating
      system's "reduce motion" setting: transforms and layout animation stop, opacity still
      crossfades, so nothing becomes invisible or unreachable — it just stops moving. Nothing in
      the app respected that setting before, and doing it per component would mean remembering it
      in each new one.

      `transition` sets the house curve as the default, so a component that specifies no
      transition gets the app's timing rather than framer's. See lib/motion for the tokens.
    */
    <MotionConfig reducedMotion="user" transition={{ duration: DURATION.base, ease: EASE }}>
    <div className="group/page w-full bg-background">
      <div className="relative flex h-screen w-full overflow-hidden">
        {/* <AppSidebar /> */}

        {/* No shadow, and nothing watching the scroll position to raise one. The chrome mask
            below separates the header from content passing under it by dissolving the content
            into it, which needs no scroll listener and is right on the first frame — the
            shadow only appeared once something had already collided with the bar.

            `minimal` on the front-of-house pages — the same variant the sign-in pages wear and
            for the same reason: there is no app behind them. See FRONT_OF_HOUSE below.

            On `/features`, which renders the same landing page from inside the app, the header
            stays full — someone who got that far has a way back. */}
        <AppHeader variant={FRONT_OF_HOUSE.has(pathname) ? "minimal" : "app"} />

        <AppFooter />

        <SidebarInset className="flex h-screen w-full flex-col items-center justify-start overflow-hidden">
          {/*
            The scrollport runs the FULL height of the viewport, under both bars, and the
            chrome mask dissolves content into them. It used to be inset by the header's height
            instead, which meant content was cut off at a hard line one pixel under the header.
            Content still comes to rest in the same place — the padding below replaces the
            margin that used to be here — but it now passes under the chrome rather than
            stopping at it.

            The scroll-padding is what keeps anchors honest: an in-page jump lands at the
            scrollport's edge, which is now behind the header, so scroll-padding moves the
            landing clear of it. It clears the WHOLE fade rather than just the bar — stopping at
            the bar's height parks the target at the foot of the ramp, scrolled to exactly the
            right place and drawn at about a tenth of full strength.
          */}
          <div
            id={MAIN_SCROLLER_ID}
            ref={scrollerRef}
            className={cn(
              "relative w-full scroll-smooth overflow-y-auto",
              "h-screen",
              "has-[[data-page-layout=bleed]]:overflow-hidden",
              // Dimensionless switches — every measurement is in lib/fade.ts — because only
              // CSS knows whether the footer slot has anything in it. They MULTIPLY, so each
              // is set by exactly one rule and the outcome never turns on which rule wins: a
              // bleed page that fills the footer gets a footer and no fade, which is a
              // combination neither flag could express on its own.
              "[--chrome-fade:1] [--chrome-foot:0]",
              "group-has-[[data-page-footer]:not(:empty)]/page:[--chrome-foot:1]",
              // The canvas draws its own full viewport and masks its own columns; a second
              // fade over the top of that would dim the same pixels twice.
              "has-[[data-page-layout=bleed]]:[--chrome-fade:0]",
            )}
            style={{
              scrollbarWidth: "none",
              maskImage: CHROME_MASK,
              WebkitMaskImage: CHROME_MASK,
              // Multiplied by the same flags as the mask, so an end that is not fading does
              // not reserve anything either.
              // Plus the header's second row while it is open (see AppHeader).
              scrollPaddingTop: `calc((${CHROME_FADE_END_PX}px + var(--chrome-row, 0px)) * var(--chrome-fade, 1))`,
              scrollPaddingBottom: `calc(${CHROME_FADE_END_PX}px * var(--chrome-foot, 0) * var(--chrome-fade, 1))`,
            }}
          >
            <div
              className={cn(
                "relative mx-auto flex w-full flex-col items-center",
                "max-w-3xl has-[[data-page-width=wide]]:max-w-6xl has-[[data-page-width=full]]:max-w-none",
                "has-[[data-page-layout=bleed]]:h-full has-[[data-page-layout=bleed]]:max-w-none",
              )}
              style={{
                /*
                  The clearance at each end, inherited from the flags the scrollport sets — so
                  a bleed page gets none, and a page with no footer reserves nothing at the
                  bottom and is as long as it needs to be.

                  The whole fade at both ends, not just the bar's height: content inside the
                  ramp is drawn at a fraction of full strength, so anything less leaves a page
                  opening on a dimmed first line and ending on a last line that can never be
                  scrolled clear. Pages that now carry too much air above their heading should
                  drop their own top padding rather than have this give back less than the
                  fade needs.
                */
                paddingTop: `calc(${CHROME_FADE_END_PX}px * var(--chrome-fade, 1))`,
                paddingBottom: `calc(${CHROME_FADE_END_PX}px * var(--chrome-foot, 0) * var(--chrome-fade, 1))`,
              }}
            >
              {children}
            </div>
          </div>
        </SidebarInset>
      </div>
    </div>
    </MotionConfig>
  )
}
